import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  BarChart3, BookOpen, BookOpenCheck, Bot, ChevronDown, CloudDownload, Database, ExternalLink, FileText, FolderKanban, GitCompareArrows, GraduationCap,
  History, Home, Landmark, LayoutDashboard, LogOut, Menu, Monitor, MonitorSmartphone, Moon, Receipt, Scale, Search, Sun, Table2, Users, Wallet, X,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { uitloggen } from '../lib/api'
import { initialen } from '../lib/opmaak'
import { navigeer, useKop, useRoute } from '../lib/router'
import { useStaat, zet } from '../lib/staat'
import { kiesThema, THEMA_LABELS, useThema, type ThemaKeuze } from '../lib/thema'
import { Logo } from './ui'
import { KiesBvs } from './stukjes'
import Zoeken from './Zoeken'
import Meldingen from './Meldingen'

/* ===========================================================================
 *  Het geraamte -- dat van het dashboard, zoals in Projects
 *
 *  Een balk van 48 hoog met links de menuknop en het logo, daarnaast de
 *  naam van het scherm, en rechts zoeken, de bv's die je bekijkt en wie je
 *  bent. De menuknop opent hetzelfde grote menu als in het dashboard.
 *
 *  Sneltoetsen: Ctrl+K of / zoeken, Esc dicht.
 * =========================================================================== */

export default function Shell({ children }: { children: ReactNode }) {
  const zoekOpen = useStaat((s) => s.zoekOpen)
  const { pad } = useRoute()
  const kop = useKop()
  const [menu, setMenu] = useState(false)

  useEffect(() => { setMenu(false) }, [pad])

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement | null
      const typt = doel && (doel.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(doel.tagName))
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        zet({ zoekOpen: true })
        return
      }
      if (typt || e.ctrlKey || e.metaKey || e.altKey) return
      if (e.key === '/') { e.preventDefault(); zet({ zoekOpen: true }) }
    }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [])

  return (
    <div className="shell">
      <header className="topbalk">
        <button type="button" className="menuknop" aria-expanded={menu} aria-label="Menu"
          onClick={() => setMenu((x) => !x)}>
          {menu ? <X size={18} /> : <Menu size={18} />}
        </button>
        <button type="button" className="topbalk-merk" onClick={() => navigeer('/')} title="Naar je overzicht">
          <Logo breedte={96} />
          <span className="topbalk-app">Exact</span>
        </button>
        {kop.titel && (
          <div className="topbalk-plek">
            <span className="topbalk-punt">·</span>
            <b>{kop.titel}</b>
            {kop.sub && <span className="topbalk-sub">{kop.sub}</span>}
          </div>
        )}
        <span className="topbalk-rek" />
        <div className="topbalk-rechts">
          <button type="button" className="search-trigger" onClick={() => zet({ zoekOpen: true })} aria-label="Zoeken">
            <Search size={15} />
            <span className="zoek-label">Zoeken…</span>
            <kbd>Ctrl K</kbd>
          </button>
          <span className="verberg-smal"><KiesBvs /></span>
          <IkMenu />
        </div>
      </header>

      <main id="inhoud" className="werkvlak">{children}</main>

      {menu && <Lanceerder onSluit={() => setMenu(false)} />}
      {zoekOpen && <Zoeken />}
      <Meldingen />
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Het grote menu, zoals in het dashboard
 * ------------------------------------------------------------------ */

interface Plek { naar: string; label: string; icoon: ReactNode; extern?: boolean }

function Lanceerder({ onSluit }: { onSluit: () => void }) {
  const management = useStaat((s) => s.management)
  const catalogus = useStaat((s) => s.catalogus)
  const { pad } = useRoute()
  const [filter, setFilter] = useState('')
  const veld = useRef<HTMLInputElement>(null)

  useEffect(() => {
    veld.current?.focus()
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') onSluit() }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [onSluit])

  const groepen = useMemo(() => {
    const g: { titel: string; plekken: Plek[] }[] = [
      { titel: 'Inzicht', plekken: [
        { naar: '/', label: 'Start', icoon: <Home size={17} /> },
        /* Verhuisd uit het dashboard. Deze drie staan vooraan omdat het de
           vraag is waarmee je 's ochtends binnenkomt: hoe staan we ervoor.
           Het grootboek eronder is waar je heen gaat als dat antwoord je niet
           bevalt. */
        { naar: '/inzicht', label: 'Managementoverzicht', icoon: <LayoutDashboard size={17} /> },
        { naar: '/groep', label: 'De groep', icoon: <BarChart3 size={17} /> },
        { naar: '/rapportage', label: 'Rapportage', icoon: <Table2 size={17} /> },
        { naar: '/grootboek', label: 'Grootboek', icoon: <BookOpen size={17} /> },
        { naar: '/vergelijken', label: 'Vergelijken', icoon: <GitCompareArrows size={17} /> },
        { naar: '/aansluiting', label: 'Aansluiting', icoon: <Scale size={17} /> },
        { naar: '/relaties', label: 'Relaties', icoon: <Users size={17} /> },
      ] },
      { titel: 'Uitpluizen', plekken: [
        { naar: '/v/boekingen', label: 'Boekingsregels', icoon: <Table2 size={17} /> },
        { naar: '/v/inkoopboekingen', label: 'Inkoopboekingen', icoon: <FileText size={17} /> },
        { naar: '/v/verkoopboekingen', label: 'Verkoopboekingen', icoon: <FileText size={17} /> },
        { naar: '/v/openstaand-crediteuren', label: 'Openstaand leveranciers', icoon: <Wallet size={17} /> },
        { naar: '/v/openstaand-debiteuren', label: 'Openstaand klanten', icoon: <Wallet size={17} /> },
        { naar: '/v/bankregels', label: 'Bankregels', icoon: <Landmark size={17} /> },
        { naar: '/v/saldi', label: 'Saldi per periode', icoon: <Receipt size={17} /> },
        ...catalogus.filter((c) => !['boekingen', 'inkoopboekingen', 'verkoopboekingen', 'openstaand-crediteuren', 'openstaand-debiteuren', 'bankregels', 'saldi'].includes(c.sleutel))
          .map((c) => ({ naar: `/v/${c.sleutel}`, label: c.naam, icoon: <Table2 size={17} /> })),
      ] },
      { titel: 'Exact', plekken: [
        { naar: '/live', label: 'Live uit Exact', icoon: <CloudDownload size={17} /> },
        { naar: '/archief', label: 'Archief', icoon: <Database size={17} /> },
        ...(management ? [{ naar: '/logboek', label: 'Logboek', icoon: <History size={17} /> }] : []),
      ] },
      { titel: 'Truckwash1', plekken: [
        { naar: 'https://truckwash-workspace.com/', label: 'Het dashboard', icoon: <LayoutDashboard size={17} />, extern: true },
        { naar: 'https://cowork.truckwash-workspace.com/', label: 'Cowork', icoon: <Bot size={17} />, extern: true },
        { naar: 'https://projects.truckwash-workspace.com/', label: 'Projects', icoon: <FolderKanban size={17} />, extern: true },
        { naar: 'https://it.truckwash-workspace.com/', label: 'IT', icoon: <MonitorSmartphone size={17} />, extern: true },
        { naar: 'https://academy.truckwash-workspace.com/', label: 'Academy', icoon: <GraduationCap size={17} />, extern: true },
      ] },
    ]
    const f = filter.trim().toLowerCase()
    return f ? g.map((x) => ({ ...x, plekken: x.plekken.filter((p) => p.label.toLowerCase().includes(f)) })).filter((x) => x.plekken.length) : g
  }, [management, catalogus, filter])

  const huidig = (naar: string) => (naar === '/' ? pad === '/' : pad === naar || pad.startsWith(naar + '/'))

  function ga(p: Plek) {
    if (p.extern) { window.open(p.naar, '_blank', 'noopener,noreferrer'); return }
    onSluit()
    navigeer(p.naar)
  }

  return createPortal(
    <>
      <div className="launcher-sluier" onClick={onSluit} />
      <nav className="launcher" aria-label="Alles in Exact">
        <div className="launcher-kop">
          <h2><BookOpenCheck size={16} /> Exact</h2>
          <p>Alles uit Exact tot op de regel: doorklikken, vergelijken en exporteren.</p>
          <div className="zoekveld launcher-filter">
            <Search size={15} />
            <input ref={veld} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter dit menu…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const eerste = groepen[0]?.plekken[0]
                  if (eerste) ga(eerste)
                }
              }} />
          </div>
        </div>
        <div className="launcher-body">
          {groepen.map((g) => (
            <div key={g.titel} className="launcher-groep">
              <h3>{g.titel}</h3>
              {g.plekken.map((p) => (
                <button key={p.naar} type="button" className="launcher-item" aria-current={huidig(p.naar) ? 'page' : undefined}
                  onClick={() => ga(p)}>
                  {p.icoon}
                  <span className="naam">{p.label}</span>
                  {p.extern && <ExternalLink size={13} />}
                </button>
              ))}
            </div>
          ))}
          {!groepen.length && <p className="stil">Niets gevonden voor “{filter}”.</p>}
        </div>
      </nav>
    </>,
    document.body,
  )
}

/* ------------------------------------------------------------------ *
 *  Wie je bent, het thema, en weg
 * ------------------------------------------------------------------ */

function IkMenu() {
  const ik = useStaat((s) => s.ik)!
  const divisies = useStaat((s) => s.divisies)
  const { keuze } = useThema()
  const [open, setOpen] = useState(false)
  const knop = useRef<HTMLButtonElement>(null)
  const [plek, setPlek] = useState<{ top: number; right: number } | null>(null)

  useEffect(() => {
    if (!open || !knop.current) return
    const r = knop.current.getBoundingClientRect()
    setPlek({ top: r.bottom + 6, right: window.innerWidth - r.right })
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [open])

  const ICOON: Record<ThemaKeuze, ReactNode> = { systeem: <Monitor size={15} />, licht: <Sun size={15} />, donker: <Moon size={15} /> }

  return (
    <>
      <button ref={knop} type="button" className={`menu-trigger ${open ? 'open' : ''}`} onClick={() => setOpen((x) => !x)}
        aria-label="Jouw menu">
        <span className="menu-av">{initialen(ik.naam)}</span>
        <ChevronDown size={14} className="pijl" />
      </button>
      {open && plek && createPortal(
        <>
          <div className="uitklap-vang" onMouseDown={() => setOpen(false)} />
          <div className="menu-panel" style={{ top: plek.top, right: plek.right }}>
            <div className="menu-persoon-kop">
              <strong>{ik.naam}</strong>
              <span>{[ik.email, `${divisies.length} bv's`].filter(Boolean).join(' · ')}</span>
            </div>
            <div className="menu-scheiding" />
            <div className="menu-groep-titel">Weergave</div>
            {(['systeem', 'licht', 'donker'] as ThemaKeuze[]).map((k) => (
              <button key={k} type="button" className={`menu-item ${keuze === k ? 'aan' : ''}`} onClick={() => kiesThema(k)}>
                <span className="ico">{ICOON[k]}</span>
                <span className="tekst"><strong>{THEMA_LABELS[k]}</strong></span>
                {keuze === k && <span className="menu-vink">✓</span>}
              </button>
            ))}
            <div className="menu-scheiding" />
            <a className="menu-item" href="https://truckwash-workspace.com/" target="_blank" rel="noopener noreferrer">
              <span className="ico"><LayoutDashboard size={15} /></span>
              <span className="tekst"><strong>Naar het dashboard</strong></span>
            </a>
            <button type="button" className="menu-item danger" onClick={() => { void uitloggen().then(() => location.assign('/')) }}>
              <span className="ico"><LogOut size={15} /></span>
              <span className="tekst"><strong>Uitloggen</strong></span>
            </button>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
